import { useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  centsToUsdString,
  parseUsdToCents,
  type GetGiftCardResponse,
  type GiftCardBatchSummary,
  type GiftCardStatus,
  type IssueGiftCardRequest,
  type IssueGiftCardResponse,
  type ListGiftCardsQuery,
  type RedeemGiftCardOnBehalfResponse,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCloseGiftCardBatch,
  useCreateGiftCardBatch,
  useGiftCard,
  useGiftCardBatches,
  useGiftCardList,
  useIssueGiftCard,
  useRedeemOnBehalf,
  useReopenGiftCardBatch,
  useRevokeGiftCard,
} from '../api/useGiftCards.ts';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { CustomerRef } from '../components/identity/CustomerRef.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { Modal } from '../components/Modal.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { auditDeepLink } from '../lib/auditDeepLink.ts';
import { useClipboard } from '../hooks/useClipboard.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  FormField,
  Input,
  KpiCard,
  PageHeader,
  Pagination,
  Panel,
  Select,
  TBody,
  THead,
  Table,
  TableEmpty,
  Td,
  Th,
  Tr,
} from '../ui/index.ts';

const UNBATCHED_SENTINEL = '__unbatched';
// ui/Select (Radix) forbids empty-string item values; this local
// sentinel stands in for "no batch selected" and is mapped back to ''
// at the state boundary so query-building behaviour is unchanged.
const NO_BATCH_SENTINEL = '__none';
const ALL_BATCHES_SENTINEL = '__all';
const DEFAULT_LIMIT = 25;

const STATUS_FILTER_VALUES = ['all', 'active', 'expired', 'redeemed', 'revoked'] as const;
type StatusFilter = (typeof STATUS_FILTER_VALUES)[number];

function isStatusFilter(value: string): value is StatusFilter {
  return (STATUS_FILTER_VALUES as readonly string[]).includes(value);
}

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CNY_FORMATTER = new Intl.NumberFormat('zh-CN', {
  style: 'currency',
  currency: 'CNY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsdCents(cents: number): string {
  if (!Number.isFinite(cents)) return '-';
  return USD_FORMATTER.format(cents / 100);
}

function formatCnyCents(cents: number): string {
  if (!Number.isFinite(cents)) return '-';
  return CNY_FORMATTER.format(cents / 100);
}

function formatUsdInput(cents: number): string {
  return centsToUsdString(cents);
}

function parseCnyToCents(raw: unknown): number | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    return Math.round(raw * 100);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100);
  }
  return null;
}

function formatRate(rate: number): string {
  if (!Number.isFinite(rate)) return '-';
  return rate.toFixed(4);
}

function formatDateTime(value: string | null): string {
  return formatLocalTimestamp(value, { fallback: '-' });
}

function maskCode(code: string): string {
  if (code.length <= 8) return '••••';
  return `${code.slice(0, 4)}••••••••${code.slice(-4)}`;
}

function statusVariant(status: GiftCardStatus): 'ok' | 'warning' | 'error' | 'neutral' {
  if (status === 'active') return 'ok';
  if (status === 'revoked') return 'error';
  if (status === 'redeemed') return 'neutral';
  return 'warning';
}

function GiftCardStatusChip({ status }: { readonly status: GiftCardStatus }): JSX.Element {
  const t = useT();
  const label =
    status === 'active'
      ? t('gift_cards.chip.active')
      : status === 'expired'
        ? t('gift_cards.chip.expired')
        : status === 'redeemed'
          ? t('gift_cards.chip.redeemed')
          : t('gift_cards.chip.revoked');
  return (
    <StatusChip
      variant={statusVariant(status)}
      label={label}
      testId="gift-card-status"
    />
  );
}

function batchLabel(
  batchId: string | null,
  batchName: string | null | undefined,
  t: ReturnType<typeof useT>,
): string {
  if (batchId === null) return t('gift_cards.batch.unbatched');
  if (batchName !== null && batchName !== undefined && batchName.length > 0) {
    return `${batchName} (${batchId})`;
  }
  return batchId;
}

function dateInputToIso(value: string, endOfDay: boolean): string | undefined {
  if (value.length === 0) return undefined;
  return `${value}T${endOfDay ? '23:59:59' : '00:00:00'}Z`;
}

/**
 * Redeemer account id as a link into the account detail page
 * (`/customers/accounts/:id`, issue #854). Renders a plain dash when
 * the card has not been redeemed.
 */
function RedeemedByAccountLink({
  accountId,
  testId,
}: {
  readonly accountId: string | null;
  readonly testId?: string;
}): JSX.Element {
  if (accountId === null) {
    return <>-</>;
  }
  return (
    <Link
      to={`/customers/accounts/${encodeURIComponent(accountId)}`}
      title={accountId}
      className="transition-colors duration-120 hover:text-signal focus-visible:outline-none focus-visible:underline"
      data-testid={testId}
    >
      <CustomerRef accountId={accountId} />
    </Link>
  );
}

export function GiftCards(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const batches = useGiftCardBatches();
  const activeBatches = useMemo(
    () => (batches.data?.batches ?? []).filter((batch) => batch.status === 'active'),
    [batches.data?.batches],
  );

  return (
    <section data-testid="gift-cards-page" className="flex flex-col gap-6">
      <PageHeader title={t('gift_cards.title')} sub={t('gift_cards.subtitle')} />

      <BatchWorkbench batches={batches.data?.batches ?? []} loading={batches.isLoading} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.6fr)]">
        <IssueCard activeBatches={activeBatches} />
        <GiftCardList batches={batches.data?.batches ?? []} />
      </div>

      <LookupCard />
    </section>
  );
}

// ─── Batches ─────────────────────────────────────────────────────────────

function BatchWorkbench({
  batches,
  loading,
}: {
  readonly batches: readonly GiftCardBatchSummary[];
  readonly loading: boolean;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const create = useCreateGiftCardBatch();
  const close = useCloseGiftCardBatch();
  const reopen = useReopenGiftCardBatch();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const totals = useMemo(
    () => ({
      issued: batches.reduce((sum, batch) => sum + batch.issued_count, 0),
      active: batches.reduce((sum, batch) => sum + batch.active_count, 0),
      redeemed: batches.reduce((sum, batch) => sum + batch.redeemed_count, 0),
      value: batches.reduce((sum, batch) => sum + batch.total_amount_cny_cents, 0),
    }),
    [batches],
  );

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError(t('gift_cards.batches.error.name_required'));
      return;
    }
    try {
      await create.mutateAsync({
        name: trimmed,
        ...(description.trim().length > 0 ? { description: description.trim() } : {}),
      });
      setName('');
      setDescription('');
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  async function updateBatch(id: string, action: 'close' | 'reopen'): Promise<void> {
    try {
      if (action === 'close') {
        await close.mutateAsync(id);
      } else {
        await reopen.mutateAsync(id);
      }
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      toast.error(message);
    }
  }

  return (
    <Panel caption={t('gift_cards.batches.title')} testId="gift-card-batches-panel">
      <div className="flex flex-col gap-4 p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KpiCard label={t('gift_cards.batches.metric.issued')} value={String(totals.issued)} />
          <KpiCard label={t('gift_cards.batches.metric.active')} value={String(totals.active)} />
          <KpiCard label={t('gift_cards.batches.metric.redeemed')} value={String(totals.redeemed)} />
          <KpiCard label={t('gift_cards.batches.metric.value')} value={formatCnyCents(totals.value)} />
        </div>

        <form
          className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(160px,0.7fr)_minmax(220px,1fr)_auto]"
          onSubmit={handleCreate}
        >
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder={t('gift_cards.batches.create.name')}
            aria-label={t('gift_cards.batches.create.name')}
            data-testid="gift-card-batch-name"
            disabled={create.isPending}
          />
          <Input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            placeholder={t('gift_cards.batches.create.description')}
            aria-label={t('gift_cards.batches.create.description')}
            data-testid="gift-card-batch-description"
            disabled={create.isPending}
          />
          <Button
            type="submit"
            variant="primary"
            className="h-9"
            loading={create.isPending}
            data-testid="gift-card-batch-create"
          >
            {create.isPending ? t('gift_cards.batches.create.submitting') : t('gift_cards.batches.create.submit')}
          </Button>
        </form>
        {error !== null ? (
          <p className="m-0 text-label text-bad" role="alert">
            {error}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
          {loading ? (
            <p className="m-0 text-body text-paper-low">{t('common.loading')}</p>
          ) : batches.length === 0 ? (
            <p className="m-0 text-body text-paper-low">{t('gift_cards.batches.empty')}</p>
          ) : (
            batches.map((batch) => (
              <div key={batch.id} className="flex min-h-32 flex-col gap-3 rounded-lg bg-well p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="m-0 truncate text-body font-medium text-paper">{batch.name}</p>
                    <p className="m-0 break-all font-mono text-label text-paper-low">{batch.id}</p>
                  </div>
                  <StatusChip
                    variant={batch.status === 'active' ? 'ok' : 'neutral'}
                    label={batch.status === 'active' ? t('gift_cards.batches.status.active') : t('gift_cards.batches.status.closed')}
                  />
                </div>
                {batch.description !== null ? (
                  <p className="m-0 text-dense text-paper-mid">{batch.description}</p>
                ) : null}
                <dl className="m-0 grid grid-cols-3 gap-2">
                  <BatchStat label={t('gift_cards.batches.metric.issued')} value={String(batch.issued_count)} />
                  <BatchStat label={t('gift_cards.batches.metric.active')} value={String(batch.active_count)} />
                  <BatchStat label={t('gift_cards.batches.metric.redeemed')} value={String(batch.redeemed_count)} />
                </dl>
                <div className="mt-auto flex items-center justify-between gap-3">
                  <span className="font-mono text-label text-paper-low">{formatCnyCents(batch.total_amount_cny_cents)}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={close.isPending || reopen.isPending}
                    onClick={() => void updateBatch(batch.id, batch.status === 'active' ? 'close' : 'reopen')}
                    data-testid={`gift-card-batch-${batch.status === 'active' ? 'close' : 'reopen'}`}
                  >
                    {batch.status === 'active' ? t('gift_cards.batches.close') : t('gift_cards.batches.reopen')}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Panel>
  );
}

function BatchStat({ label, value }: { readonly label: string; readonly value: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-label text-paper-low">{label}</dt>
      <dd className="m-0 font-mono text-dense text-paper">{value}</dd>
    </div>
  );
}

// ─── Issue ───────────────────────────────────────────────────────────────

function IssueCard({ activeBatches }: { readonly activeBatches: readonly GiftCardBatchSummary[] }): JSX.Element {
  const t = useT();
  const issue = useIssueGiftCard();
  const toast = useToast();
  const [codeHint, setCodeHint] = useState('');
  const [amountCnyInput, setAmountCnyInput] = useState('');
  const [batchId, setBatchId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssueGiftCardResponse | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const amountCnyCents = parseCnyToCents(amountCnyInput);
    if (amountCnyCents === null || amountCnyCents <= 0) {
      setError(t('gift_cards.issue.error.amount_positive'));
      return;
    }
    if (amountCnyCents % 100 !== 0) {
      setError(t('gift_cards.issue.error.amount_whole_yuan'));
      return;
    }
    const isoExpiry = dateInputToIso(expiresAt.trim(), true);
    if (isoExpiry !== undefined && Date.parse(isoExpiry) <= Date.now()) {
      setError(t('gift_cards.issue.error.expires_at_future'));
      return;
    }
    const body: IssueGiftCardRequest = {
      amount_cny_cents: amountCnyCents,
      ...(codeHint.trim().length > 0 ? { code_hint: codeHint.trim() } : {}),
      ...(batchId.length > 0 ? { batch_id: batchId } : {}),
      ...(isoExpiry !== undefined ? { expires_at: isoExpiry } : {}),
    };
    try {
      const result = await issue.mutateAsync(body);
      setIssued(result);
      setCodeHint('');
      setAmountCnyInput('');
      setExpiresAt('');
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Panel caption={t('gift_cards.issue.title')} testId="gift-card-issue-card">
      <div className="flex flex-col gap-4 p-4">
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <FormField label={t('gift_cards.issue.amount.label')} fieldId="gift-card-issue-amount">
            <Input
              id="gift-card-issue-amount"
              type="number"
              min="0"
              step="1"
              mono
              value={amountCnyInput}
              onChange={(e) => setAmountCnyInput(e.currentTarget.value)}
              placeholder={t('gift_cards.issue.amount.placeholder')}
              data-testid="gift-card-issue-amount"
              disabled={issue.isPending}
            />
          </FormField>
          <FormField label={t('gift_cards.issue.batch_id.label')} fieldId="gift-card-issue-batch-id">
            <Select
              value={batchId.length > 0 ? batchId : NO_BATCH_SENTINEL}
              onValueChange={(value) => setBatchId(value === NO_BATCH_SENTINEL ? '' : value)}
              options={[
                { value: NO_BATCH_SENTINEL, label: t('gift_cards.batch.unbatched') },
                ...activeBatches.map((batch) => ({ value: batch.id, label: batch.name })),
              ]}
              ariaLabel={t('gift_cards.issue.batch_id.label')}
              disabled={issue.isPending}
              testId="gift-card-issue-batch-id"
            />
          </FormField>
          <FormField label={t('gift_cards.issue.code_hint.label')} fieldId="gift-card-issue-code-hint">
            <Input
              id="gift-card-issue-code-hint"
              type="text"
              value={codeHint}
              onChange={(e) => setCodeHint(e.currentTarget.value)}
              placeholder={t('gift_cards.issue.code_hint.placeholder')}
              data-testid="gift-card-issue-code-hint"
              disabled={issue.isPending}
            />
          </FormField>
          <FormField label={t('gift_cards.issue.expires_at.label')} fieldId="gift-card-issue-expires-at">
            <Input
              id="gift-card-issue-expires-at"
              type="date"
              mono
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.currentTarget.value)}
              data-testid="gift-card-issue-expires-at"
              disabled={issue.isPending}
            />
          </FormField>
          {error !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="gift-card-issue-error">
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            className="h-9"
            loading={issue.isPending}
            data-testid="gift-card-issue-submit"
          >
            {issue.isPending ? t('gift_cards.issue.submitting') : t('gift_cards.issue.submit')}
          </Button>
        </form>
        {issued !== null ? (
          <IssuedCardSummary
            issued={issued}
            onIssueAnother={() => {
              setIssued(null);
              setCodeHint('');
              setAmountCnyInput('');
              setExpiresAt('');
            }}
          />
        ) : null}
      </div>
    </Panel>
  );
}

function IssuedCardSummary({
  issued,
  onIssueAnother,
}: {
  readonly issued: IssueGiftCardResponse;
  readonly onIssueAnother: () => void;
}): JSX.Element {
  const t = useT();
  const { copy, copied } = useClipboard();
  return (
    <div className="flex flex-col gap-3 rounded-lg bg-well p-4" data-testid="gift-card-issue-result">
      {/* Block-level click-to-copy affordance — intentionally not ui/Button
          (which is an inline control); styled with canonical tokens. */}
      <button
        type="button"
        onClick={() => void copy(issued.code)}
        data-testid="gift-card-issue-code"
        className="rounded-lg bg-card p-3 text-left transition-colors duration-120 hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
      >
        <span className="block text-label text-paper-low">{t('gift_cards.issued.code_label')}</span>
        <span className="mt-1 block break-all font-mono text-body font-semibold text-signal">{issued.code}</span>
      </button>
      <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <BatchStat label={t('gift_cards.issued.face_value')} value={formatCnyCents(issued.amount_cny_cents)} />
        <BatchStat label={t('gift_cards.issued.credit_value')} value={formatUsdCents(issued.amount_usd_cents)} />
        <BatchStat label={t('gift_cards.issued.applied_rate')} value={formatRate(issued.applied_rate)} />
        <BatchStat label={t('gift_cards.issued.batch')} value={issued.batch_id ?? t('gift_cards.batch.unbatched')} />
        <BatchStat label={t('gift_cards.issued.expires_at')} value={formatDateTime(issued.expires_at)} />
      </dl>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-label text-paper-low" data-testid="gift-card-issue-copy-status">
          {copied ? t('common.copied') : t('common.click_to_copy')}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={onIssueAnother}
          data-testid="gift-card-issue-another"
        >
          {t('gift_cards.issued.issue_another')}
        </Button>
      </div>
    </div>
  );
}

// ─── List ────────────────────────────────────────────────────────────────

function GiftCardList({ batches }: { readonly batches: readonly GiftCardBatchSummary[] }): JSX.Element {
  const t = useT();
  const { copy } = useClipboard();
  const [batchId, setBatchId] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [accountId, setAccountId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());
  const [selectedCard, setSelectedCard] = useState<GetGiftCardResponse | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);

  const query = useMemo<ListGiftCardsQuery>(() => {
    const fromIso = dateInputToIso(from, false);
    const toIso = dateInputToIso(to, true);
    return {
      ...(batchId.length > 0 ? { batch_id: batchId } : {}),
      ...(status !== 'all' ? { status } : {}),
      ...(accountId.trim().length > 0 ? { account_id: accountId.trim() } : {}),
      ...(fromIso !== undefined ? { from: fromIso } : {}),
      ...(toIso !== undefined ? { to: toIso } : {}),
      limit: DEFAULT_LIMIT,
      offset,
    };
  }, [accountId, batchId, from, offset, status, to]);

  const cards = useGiftCardList(query);
  const total = cards.data?.total ?? 0;
  const rows = cards.data?.gift_cards ?? [];
  const page = Math.floor(offset / DEFAULT_LIMIT) + 1;
  const pageCount = Math.max(1, Math.ceil(total / DEFAULT_LIMIT));

  function resetPagination(): void {
    setOffset(0);
  }

  function toggleReveal(code: string): void {
    setRevealed((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function openRevoke(card: GetGiftCardResponse): void {
    setSelectedCard(card);
    setRevokeOpen(true);
  }

  function openRedeem(card: GetGiftCardResponse): void {
    setSelectedCard(card);
    setRedeemOpen(true);
  }

  return (
    <Panel caption={t('gift_cards.list.title')} testId="gift-card-list-panel">
      <div className="flex flex-col gap-4 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <Select
            value={batchId.length > 0 ? batchId : ALL_BATCHES_SENTINEL}
            onValueChange={(value) => {
              setBatchId(value === ALL_BATCHES_SENTINEL ? '' : value);
              resetPagination();
            }}
            options={[
              { value: ALL_BATCHES_SENTINEL, label: t('gift_cards.list.batch.all') },
              { value: UNBATCHED_SENTINEL, label: t('gift_cards.batch.unbatched') },
              ...batches.map((batch) => ({ value: batch.id, label: batch.name })),
            ]}
            ariaLabel={t('gift_cards.list.column.batch')}
            testId="gift-card-list-batch-filter"
          />
          <Select
            value={status}
            onValueChange={(value) => {
              if (isStatusFilter(value)) {
                setStatus(value);
                resetPagination();
              }
            }}
            options={[
              { value: 'all', label: t('gift_cards.list.status.all') },
              { value: 'active', label: t('gift_cards.chip.active') },
              { value: 'expired', label: t('gift_cards.chip.expired') },
              { value: 'redeemed', label: t('gift_cards.chip.redeemed') },
              { value: 'revoked', label: t('gift_cards.chip.revoked') },
            ]}
            ariaLabel={t('gift_cards.list.column.status')}
            testId="gift-card-list-status-filter"
          />
          <Input
            type="text"
            mono
            value={accountId}
            onChange={(e) => {
              setAccountId(e.currentTarget.value);
              resetPagination();
            }}
            placeholder={t('gift_cards.list.account.placeholder')}
            aria-label={t('gift_cards.list.account.placeholder')}
            data-testid="gift-card-list-account-filter"
          />
          <Input
            type="date"
            mono
            value={from}
            onChange={(e) => {
              setFrom(e.currentTarget.value);
              resetPagination();
            }}
            data-testid="gift-card-list-from-filter"
          />
          <Input
            type="date"
            mono
            value={to}
            onChange={(e) => {
              setTo(e.currentTarget.value);
              resetPagination();
            }}
            data-testid="gift-card-list-to-filter"
          />
        </div>

        {cards.isError ? (
          <p
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/40 px-4 py-3 text-body text-bad-bright"
            role="alert"
          >
            {cards.error instanceof OpsApiError ? `${cards.error.code}: ${cards.error.message}` : coerceErrorMessage(cards.error)}
          </p>
        ) : null}

        <Table>
          <THead>
            <Tr interactive={false}>
              <Th>{t('gift_cards.list.column.code')}</Th>
              <Th>{t('gift_cards.list.column.status')}</Th>
              <Th num>{t('gift_cards.list.column.amount')}</Th>
              <Th>{t('gift_cards.list.column.batch')}</Th>
              <Th>{t('gift_cards.list.column.redeemed_by')}</Th>
              <Th>{t('gift_cards.list.column.updated')}</Th>
              <Th className="text-right">{t('gift_cards.list.column.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {cards.isLoading ? (
              <TableEmpty colSpan={7}>{t('common.loading')}</TableEmpty>
            ) : rows.length === 0 ? (
              <TableEmpty colSpan={7}>{t('gift_cards.list.empty')}</TableEmpty>
            ) : (
              rows.map((card) => {
                const isRevealed = revealed.has(card.code);
                const canRevoke = card.status === 'active' || card.status === 'expired';
                const canRedeem = card.status === 'active';
                return (
                  <Tr key={card.code}>
                    <Td>
                      <div className="flex min-w-56 items-center gap-2">
                        <span
                          className="break-all font-mono text-label text-paper"
                          data-testid="gift-card-list-code"
                        >
                          {isRevealed ? card.code : maskCode(card.code)}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => toggleReveal(card.code)}>
                          {isRevealed ? t('gift_cards.list.hide_code') : t('gift_cards.list.show_code')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => void copy(card.code)}>
                          {t('gift_cards.list.copy_code')}
                        </Button>
                      </div>
                    </Td>
                    <Td>
                      <GiftCardStatusChip status={card.status} />
                    </Td>
                    <Td num emphasis>
                      {formatCnyCents(card.amount_cny_cents)}
                    </Td>
                    <Td>{batchLabel(card.batch_id, card.batch_name, t)}</Td>
                    <Td mono>
                      <RedeemedByAccountLink
                        accountId={card.redeemed_by_account_id}
                        testId={`gift-card-list-redeemed-by-${card.code}`}
                      />
                    </Td>
                    <Td mono>{formatDateTime(card.redeemed_at ?? card.revoked_at ?? card.issued_at)}</Td>
                    <Td>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={!canRevoke}
                          onClick={() => openRevoke(card)}
                        >
                          {t('gift_cards.result.revoke')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={!canRedeem}
                          onClick={() => openRedeem(card)}
                        >
                          {t('gift_cards.result.redeem')}
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                );
              })
            )}
          </TBody>
        </Table>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-label text-paper-low">
            {t('gift_cards.list.total', { total: String(total) })}
          </span>
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(next) => setOffset((next - 1) * DEFAULT_LIMIT)}
            prevLabel={t('common.previous')}
            nextLabel={t('common.next')}
            ariaLabel={t('accounts.pagination.aria_label')}
          />
        </div>

        {revokeOpen && selectedCard !== null ? (
          <RevokeModal card={selectedCard} onClose={() => setRevokeOpen(false)} onSuccess={() => setRevokeOpen(false)} />
        ) : null}
        {redeemOpen && selectedCard !== null ? (
          <RedeemModal card={selectedCard} onClose={() => setRedeemOpen(false)} onSuccess={() => setRedeemOpen(false)} />
        ) : null}
      </div>
    </Panel>
  );
}

// ─── Lookup ──────────────────────────────────────────────────────────────

function LookupCard(): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);
  const lookup = useGiftCard(submitted ?? undefined, { enabled: submitted !== null });
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = draft.trim();
    if (trimmed.length > 0) setSubmitted(trimmed);
  }

  return (
    <Panel caption={t('gift_cards.lookup.title')} testId="gift-card-lookup-card">
      <div className="flex flex-col gap-4 p-4">
        <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={handleSubmit} noValidate>
          <FormField
            label={t('gift_cards.lookup.code.label')}
            fieldId="gift-card-lookup-input"
            className="flex-1"
          >
            <Input
              id="gift-card-lookup-input"
              type="text"
              mono
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              placeholder={t('gift_cards.lookup.code.placeholder')}
              data-testid="gift-card-lookup-input"
              disabled={lookup.isFetching}
              autoComplete="off"
            />
          </FormField>
          <Button
            type="submit"
            variant="primary"
            className="h-9"
            disabled={draft.trim().length === 0}
            loading={lookup.isFetching}
            data-testid="gift-card-lookup-submit"
          >
            {lookup.isFetching ? t('gift_cards.lookup.submitting') : t('gift_cards.lookup.submit')}
          </Button>
        </form>

        {submitted !== null && lookup.isError ? (
          <p
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/40 px-4 py-3 text-body text-bad-bright"
            role="alert"
            data-testid="gift-card-lookup-error"
          >
            {lookup.error instanceof OpsApiError ? `${lookup.error.code}: ${lookup.error.message}` : t('gift_cards.lookup.error.network')}
          </p>
        ) : null}

        {submitted !== null && lookup.isSuccess ? (
          <LookupResult
            card={lookup.data}
            onRevoke={() => setRevokeOpen(true)}
            onRedeem={() => setRedeemOpen(true)}
          />
        ) : null}

        {revokeOpen && lookup.data !== undefined ? (
          <RevokeModal
            card={lookup.data}
            onClose={() => setRevokeOpen(false)}
            onSuccess={() => setRevokeOpen(false)}
          />
        ) : null}
        {redeemOpen && lookup.data !== undefined ? (
          <RedeemModal
            card={lookup.data}
            onClose={() => setRedeemOpen(false)}
            onSuccess={() => setRedeemOpen(false)}
          />
        ) : null}
      </div>
    </Panel>
  );
}

function LookupResult({
  card,
  onRevoke,
  onRedeem,
}: {
  readonly card: GetGiftCardResponse;
  readonly onRevoke: () => void;
  readonly onRedeem: () => void;
}): JSX.Element {
  const t = useT();
  const canRevoke = card.status === 'active' || card.status === 'expired';
  const canRedeem = card.status === 'active';

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-well p-4" data-testid="gift-card-lookup-result">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="break-all font-mono text-body font-semibold text-signal" data-testid="gift-card-lookup-code">
          {card.code}
        </span>
        <GiftCardStatusChip status={card.status} />
      </div>
      <dl className="m-0 grid grid-cols-1 gap-3 md:grid-cols-4">
        <BatchStat label={t('gift_cards.result.face_value')} value={formatCnyCents(card.amount_cny_cents)} />
        <BatchStat label={t('gift_cards.result.credit_value')} value={formatUsdCents(card.amount_usd_cents)} />
        <BatchStat label={t('gift_cards.result.applied_rate')} value={formatRate(card.applied_rate)} />
        <BatchStat label={t('gift_cards.result.batch_id')} value={batchLabel(card.batch_id, card.batch_name, t)} />
        <BatchStat label={t('gift_cards.result.issued_at')} value={formatDateTime(card.issued_at)} />
        <BatchStat label={t('gift_cards.result.expires_at')} value={formatDateTime(card.expires_at)} />
        <BatchStat
          label={t('gift_cards.result.redeemed_by')}
          value={
            <RedeemedByAccountLink
              accountId={card.redeemed_by_account_id}
              testId="gift-card-lookup-redeemed-by"
            />
          }
        />
        <BatchStat label={t('gift_cards.list.column.source')} value={card.redeem_source ?? '-'} />
        <BatchStat label={t('gift_cards.result.redeemed_at')} value={formatDateTime(card.redeemed_at)} />
        <BatchStat label={t('gift_cards.result.revoked_at')} value={formatDateTime(card.revoked_at)} />
      </dl>
      <div className="flex justify-end gap-2">
        <Button
          variant="danger"
          disabled={!canRevoke}
          onClick={onRevoke}
          data-testid="gift-card-lookup-revoke"
        >
          {t('gift_cards.result.revoke')}
        </Button>
        <Button
          variant="secondary"
          disabled={!canRedeem}
          onClick={onRedeem}
          data-testid="gift-card-lookup-redeem"
        >
          {t('gift_cards.result.redeem')}
        </Button>
      </div>
    </div>
  );
}

// ─── Modals ──────────────────────────────────────────────────────────────

function RevokeModal({
  card,
  onClose,
  onSuccess,
}: {
  readonly card: GetGiftCardResponse;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}): JSX.Element {
  const t = useT();
  const revoke = useRevokeGiftCard();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await revoke.mutateAsync({
        code: card.code,
        body: reason.trim().length > 0 ? { reason: reason.trim() } : {},
      });
      toast.success(t('gift_cards.revoke_modal.toast', { code: card.code }), {
        action: {
          label: t('toast.action.view_audit'),
          // Audit row written by platform-api
          // `src/routes/internal-gift-cards.ts` (action
          // 'gift-card.revoke', target = the card code).
          to: auditDeepLink('gift-card.revoke', card.code),
        },
      });
      onSuccess();
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal isOpen onClose={() => { if (!revoke.isPending) onClose(); }} titleId="revoke-modal-title" testId="gift-card-revoke-modal">
      <form className="flex flex-col gap-5 p-6" onSubmit={handleSubmit} noValidate>
        <h2 id="revoke-modal-title" className="m-0 text-title text-paper">{t('gift_cards.revoke_modal.title')}</h2>
        <p className="m-0 text-body text-paper-mid">
          {t('gift_cards.revoke_modal.body_prefix')}
          <span className="font-mono font-semibold text-paper">{card.code}</span>
          {t('gift_cards.revoke_modal.body_separator')}
          {formatCnyCents(card.amount_cny_cents)}
          {t('gift_cards.revoke_modal.body_suffix')}
        </p>
        <FormField label={t('gift_cards.revoke_modal.reason.label')} fieldId="gift-card-revoke-reason">
          <Input
            id="gift-card-revoke-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder={t('gift_cards.revoke_modal.reason.placeholder')}
            data-testid="gift-card-revoke-reason"
            disabled={revoke.isPending}
          />
        </FormField>
        {error !== null ? (
          <p className="m-0 text-label text-bad" role="alert" data-testid="gift-card-revoke-error">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={revoke.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="danger"
            loading={revoke.isPending}
            data-testid="gift-card-revoke-submit"
          >
            {revoke.isPending ? t('gift_cards.revoke_modal.submitting') : t('gift_cards.revoke_modal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RedeemModal({
  card,
  onClose,
  onSuccess,
}: {
  readonly card: GetGiftCardResponse;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}): JSX.Element {
  const t = useT();
  const redeem = useRedeemOnBehalf();
  const toast = useToast();
  const [accountId, setAccountId] = useState('');
  const [confirmAmount, setConfirmAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const externalRequestId = useMemo(() => crypto.randomUUID(), []);
  const expectedAmount = card.amount_usd_cents;
  const expectedAmountStr = formatUsdInput(expectedAmount);
  const faceValue = formatCnyCents(card.amount_cny_cents);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedAccount = accountId.trim();
    if (trimmedAccount.length === 0) {
      setError(t('gift_cards.redeem_modal.error.account_required'));
      return;
    }
    const typedAmountCents = parseUsdToCents(confirmAmount);
    if (typedAmountCents === null || typedAmountCents !== expectedAmount) {
      setError(t('gift_cards.redeem_modal.error.amount_mismatch', { amount: expectedAmountStr }));
      return;
    }
    try {
      const result: RedeemGiftCardOnBehalfResponse = await redeem.mutateAsync({
        code: card.code,
        account_id: trimmedAccount,
        external_request_id: externalRequestId,
        ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
      });
      toast.success(
        t('gift_cards.redeem_modal.toast', {
          amount: formatUsdCents(result.amount_usd_cents),
          account: result.account_id,
          balance: formatUsdCents(result.new_balance_usd_cents),
        }),
        {
          action: {
            label: t('toast.action.view_audit'),
            // Audit row written by platform-api
            // `src/routes/internal-gift-cards.ts` (action
            // 'gift-card.redeem-on-behalf', target = the card code).
            to: auditDeepLink('gift-card.redeem-on-behalf', card.code),
          },
        },
      );
      onSuccess();
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal isOpen onClose={() => { if (!redeem.isPending) onClose(); }} titleId="redeem-modal-title" testId="gift-card-redeem-modal">
      <form className="flex flex-col gap-5 p-6" onSubmit={handleSubmit} noValidate>
        <h2 id="redeem-modal-title" className="m-0 text-title text-paper">{t('gift_cards.redeem_modal.title')}</h2>
        <p className="m-0 rounded-lg bg-well p-4 text-body text-paper-mid">
          {t('gift_cards.redeem_modal.body_prefix')}
          <span className="font-mono font-semibold text-ok">{formatUsdCents(expectedAmount)}</span>
          {t('gift_cards.redeem_modal.body_middle')}
          <span className="font-mono font-semibold text-paper">{card.code}</span>
          {t('gift_cards.redeem_modal.body_suffix', { faceValue })}
        </p>
        <FormField label={t('gift_cards.redeem_modal.account.label')} fieldId="gift-card-redeem-account-id">
          <Input
            id="gift-card-redeem-account-id"
            type="text"
            mono
            value={accountId}
            onChange={(e) => setAccountId(e.currentTarget.value)}
            placeholder={t('gift_cards.redeem_modal.account.placeholder')}
            data-testid="gift-card-redeem-account-id"
            disabled={redeem.isPending}
            autoComplete="off"
          />
        </FormField>
        <FormField
          label={t('gift_cards.redeem_modal.confirm.label', { amount: expectedAmountStr })}
          fieldId="gift-card-redeem-confirm-amount"
        >
          <Input
            id="gift-card-redeem-confirm-amount"
            type="text"
            inputMode="decimal"
            mono
            value={confirmAmount}
            onChange={(e) => setConfirmAmount(e.currentTarget.value)}
            placeholder={expectedAmountStr}
            data-testid="gift-card-redeem-confirm-amount"
            disabled={redeem.isPending}
            autoComplete="off"
          />
        </FormField>
        <FormField label={t('gift_cards.redeem_modal.reason.label')} fieldId="gift-card-redeem-reason">
          <Input
            id="gift-card-redeem-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder={t('gift_cards.redeem_modal.reason.placeholder')}
            data-testid="gift-card-redeem-reason"
            disabled={redeem.isPending}
          />
        </FormField>
        <p className="m-0 break-all font-mono text-micro text-paper-low">
          {t('gift_cards.redeem_modal.idempotency')} {externalRequestId}
        </p>
        {error !== null ? (
          <p className="m-0 text-label text-bad" role="alert" data-testid="gift-card-redeem-error">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={redeem.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={redeem.isPending}
            data-testid="gift-card-redeem-submit"
          >
            {redeem.isPending ? t('gift_cards.redeem_modal.submitting') : t('gift_cards.redeem_modal.submit', { amount: formatUsdCents(expectedAmount) })}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
